import { Injectable, inject } from '@angular/core';
import { from, Observable, firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';

export interface UserResponse {
  userId: string;
  tenantId: string;
  fullName: string;
  username: string;
  email: string;
  phone?: string;
  roleCodes: number[];
  roleNames: string[];
  businessUnitIds: string[];
  status: string;
  authProvider: string;
  mustChangePassword: boolean;
  lastLoginDate?: string;
  createdAt: string;
}

export interface CreateUserRequest {
  fullName: string;
  email: string;
  username: string;
  phone?: string;
  roleCodes: number[];
  businessUnitIds: string[];
}

export interface UpdateUserRequest {
  fullName: string;
  email: string;
  phone?: string;
  roleCodes: number[];
  businessUnitIds: string[];
  status: string;
}

export interface PagedResult<T> {
  data: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

@Injectable({ providedIn: 'root' })
export class UserService {
  private api = inject(ApiService);

  getUsers(params: {
    search?: string;
    roleCode?: number;
    status?: string;
    page?: number;
    pageSize?: number;
  }): Observable<any> {
    return this.api.get<PagedResult<UserResponse>>('users', params);
  }

  getUserById(userId: string): Observable<any> {
    return this.api.get<UserResponse>(`users/${userId}`);
  }

  createUser(request: CreateUserRequest): Observable<any> {
    return this.api.post<{ user: UserResponse; tempPassword: string }>('users', request);
  }

  updateUser(userId: string, request: UpdateUserRequest): Observable<any> {
    return this.api.put<UserResponse>(`users/${userId}`, request);
  }

  deactivateUser(userId: string): Observable<any> {
    return this.api.delete(`users/${userId}`);
  }

  resetPassword(userId: string): Observable<any> {
    return this.api.post<{ tempPassword: string }>(`users/${userId}/reset-password`, {});
  }

  checkUsername(username: string, excludeUserId?: string): Observable<any> {
    return this.api.get<{ available: boolean }>('users/check-username', { username, excludeUserId });
  }

  checkEmail(email: string, excludeUserId?: string): Observable<any> {
    return this.api.get<{ available: boolean }>('users/check-email', { email, excludeUserId });
  }

  inviteUser(request: { email?: string; phone?: string }): Observable<any> {
    return this.api.post<any>('users/invite', request);
  }

  getMyProfile(): Observable<any> {
    return this.api.get<SelfProfile>('users/me/profile');
  }

  updateMyContact(request: { email?: string | null; phone: string }): Observable<any> {
    return this.api.patch<any>('users/me/contact', request);
  }

  getAvatarSas(userId: string): Observable<any> {
    return this.api.get<{ url: string; expiresAt: string } | null>(`users/${userId}/avatar-sas`)
      .pipe();
  }

  requestAvatarUploadSas(
    userId: string,
    body: { contentType: string; sizeBytes: number; fileName: string }
  ): Observable<any> {
    return this.api.post<{ blobName: string; sasUrl: string; contentType: string }>(
      `users/${userId}/avatar/upload-sas`,
      body
    );
  }

  commitAvatar(userId: string, body: { blobName: string }): Observable<any> {
    return this.api.post<{ profilePhoto: string }>(`users/${userId}/avatar/commit`, body);
  }

  deleteAvatar(userId: string): Observable<any> {
    return this.api.delete(`users/${userId}/avatar`);
  }

  uploadAvatar(userId: string, file: File): Observable<any> {
    return from(this.directUploadAvatar(userId, file));
  }

  private async directUploadAvatar(userId: string, file: File): Promise<any> {
    const sasResp = await firstValueFrom(this.requestAvatarUploadSas(userId, {
      contentType: file.type,
      sizeBytes: file.size,
      fileName: file.name
    }));
    if (!sasResp?.success || !sasResp.data?.sasUrl) {
      return { success: false, message: sasResp?.message || 'Failed to obtain upload URL.' };
    }
    const slot = sasResp.data;
    const putRes = await fetch(slot.sasUrl, {
      method: 'PUT',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': slot.contentType
      },
      body: file
    });
    if (!putRes.ok) {
      return { success: false, message: `Upload failed (HTTP ${putRes.status}).` };
    }
    return firstValueFrom(this.commitAvatar(userId, { blobName: slot.blobName }));
  }
}

export interface SelfProfile {
  userId: string;
  username: string;
  fullName: string;
  email?: string | null;
  phone?: string | null;
  nic?: string | null;
  memberId?: string | null;
  roleCodes: number[];
  businessUnits: Array<{ id: string; name: string }>;
  joinedDate?: string | null;
  address?: string | null;
}
